import React, { useState, useEffect } from 'react';

const TaasOverviewPage = () => {
    const [activeTab, setActiveTab] = useState('problem');
    const [openFaq, setOpenFaq] = useState(null);
    const [formData, setFormData] = useState({
        company: '',
        name: '',
        email: '',
        phone: '',
        requirement: ''
    });
    const [formStatus, setFormStatus] = useState('idle'); // idle, submitting, success

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const scrollToSection = (id) => {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormStatus('submitting');
        // Simulate API call
        setTimeout(() => {
            setFormStatus('success');
            setFormData({ company: '', name: '', email: '', phone: '', requirement: '' });
        }, 1500);
    };

    // --- ICONS (Inline SVG) ---
    const IconCheck = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>;
    const IconX = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;
    const IconChevronDown = ({ isOpen }) => (
        <svg
            width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}
        >
            <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
    );

    // --- CSS STYLES ---
    const styles = `
    /* Scope Variables */
    .taas-wrapper {
      --taas-primary: #0a1628;
      --taas-primary-light: #1a3654;
      --taas-accent: #14b8c4;
      --taas-accent-dark: #0891a2;
      --taas-success: #10b981;
      --taas-danger: #ef4444;
      --taas-text: #111827;
      --taas-text-muted: #6b7280;
      --taas-bg: #ffffff;
      --taas-bg-alt: #f9fafb;
      --taas-border: #e5e7eb;
      --taas-radius: 8px;
    }

    .taas-wrapper {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: var(--taas-text);
      line-height: 1.6;
      background: var(--taas-bg-alt);
    }

    .taas-container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 1.5rem;
    }

    /* TYPOGRAPHY */
    .taas-h1 { font-size: 2.5rem; font-weight: 800; color: var(--taas-primary); line-height: 1.2; margin-bottom: 1rem; }
    .taas-h2 { font-size: 2rem; font-weight: 700; color: var(--taas-primary); margin-bottom: 2rem; text-align: center; }
    .taas-h3 { font-size: 1.5rem; font-weight: 600; color: var(--taas-primary); margin-bottom: 1rem; }
    .taas-lead { font-size: 1.125rem; color: var(--taas-text-muted); max-width: 800px; margin: 0 auto 2rem; }
    
    @media (max-width: 768px) {
      .taas-h1 { font-size: 2rem; }
      .taas-h2 { font-size: 1.5rem; }
    }

    /* BUTTONS */
    .taas-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.75rem 1.5rem;
      font-weight: 600;
      border-radius: var(--taas-radius);
      transition: all 0.2s;
      cursor: pointer;
      text-decoration: none;
      border: none;
      font-size: 1rem;
    }
    .taas-btn-primary {
      background: var(--taas-accent);
      color: white;
    }
    .taas-btn-primary:hover { background: var(--taas-accent-dark); }
    .taas-btn-outline {
      border: 2px solid var(--taas-primary);
      color: var(--taas-primary);
      background: transparent;
    }
    .taas-btn-outline:hover {
      background: var(--taas-primary);
      color: white;
    }

    /* NAVIGATION */
    .taas-nav {
      position: sticky;
      top: 0;
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(10px);
      border-bottom: 1px solid var(--taas-border);
      z-index: 100;
      padding: 1rem 0;
    }
    .taas-nav-list {
      display: flex;
      justify-content: center;
      gap: 2rem;
      list-style: none;
      margin: 0;
      padding: 0;
      overflow-x: auto;
      white-space: nowrap;
    }
    .taas-nav-link {
      color: var(--taas-text-muted);
      cursor: pointer;
      font-weight: 500;
      font-size: 0.9rem;
      transition: color 0.2s;
    }
    .taas-nav-link:hover { color: var(--taas-primary); }

    /* HERO */
    .taas-hero {
      padding: 6rem 0 4rem;
      background: linear-gradient(to bottom, #ffffff, #f0fdfa);
      text-align: center;
    }
    .taas-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.25rem 0.75rem;
      background: #e0f2fe;
      color: #0369a1;
      font-size: 0.875rem;
      font-weight: 600;
      border-radius: 999px;
      margin-bottom: 1.5rem;
    }

    /* SECTIONS */
    .taas-section { padding: 5rem 0; }
    .taas-section-white { background: var(--taas-bg); }
    
    /* PROBLEM / PAIN POINTS */
    .problem-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 2rem;
    }
    .problem-card {
      background: #fff1f2;
      border: 1px solid #fecdd3;
      padding: 1.5rem;
      border-radius: var(--taas-radius);
    }
    .problem-header { display: flex; align-items: center; gap: 0.75rem; color: var(--taas-danger); font-weight: 700; margin-bottom: 0.5rem; }

    /* SOLUTION */
    .solution-timeline {
      display: flex;
      flex-direction: column;
      gap: 2rem;
      position: relative;
    }
    @media(min-width: 768px) {
      .solution-timeline {
        flex-direction: row;
        justify-content: space-between;
      }
      .solution-timeline::before {
        content: '';
        position: absolute;
        top: 24px;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--taas-border);
        z-index: 0;
      }
    }
    .timeline-step {
      position: relative;
      z-index: 1;
      background: var(--taas-bg);
      flex: 1;
      padding: 1rem;
      border-radius: var(--taas-radius);
      border: 1px solid var(--taas-border);
      text-align: center;
    }
    .step-number {
      width: 48px;
      height: 48px;
      background: var(--taas-primary);
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      margin: 0 auto 1rem;
      font-size: 1.25rem;
    }

    /* COMPARISON TABLE */
    .comparison-table-wrapper {
      overflow-x: auto;
      border-radius: var(--taas-radius);
      border: 1px solid var(--taas-border);
      background: var(--taas-bg);
    }
    .taas-table {
      width: 100%;
      border-collapse: collapse;
      min-width: 600px;
    }
    .taas-table th, .taas-table td {
      padding: 1rem 1.5rem;
      text-align: left;
      border-bottom: 1px solid var(--taas-border);
    }
    .taas-table th { background: var(--taas-bg-alt); color: var(--taas-text-muted); font-weight: 600; text-transform: uppercase; font-size: 0.8rem; letter-spacing: 0.05em; }
    .col-internal { color: var(--taas-text-muted); }
    .col-codemaker { font-weight: 600; color: var(--taas-primary); background: #f0fdfa; }
    .highlight-cell { color: var(--taas-success); }
    
    /* FAQ */
    .faq-item {
      border: 1px solid var(--taas-border);
      border-radius: var(--taas-radius);
      margin-bottom: 1rem;
      background: var(--taas-bg);
      overflow: hidden;
    }
    .faq-question {
      padding: 1.5rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      font-weight: 600;
      color: var(--taas-primary);
    }
    .faq-answer {
      padding: 0 1.5rem 1.5rem;
      color: var(--taas-text-muted);
      border-top: 1px solid var(--taas-border);
      background: #f8fafc;
    }

    /* CONTACT FORM */
    .form-card {
      background: var(--taas-bg);
      padding: 2rem;
      border-radius: var(--taas-radius);
      box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
      max-width: 600px;
      margin: 0 auto;
    }
    .form-group { margin-bottom: 1.5rem; }
    .form-label { display: block; font-weight: 600; margin-bottom: 0.5rem; color: var(--taas-primary); }
    .form-input { 
      width: 100%; 
      padding: 0.75rem; 
      border: 1px solid var(--taas-border); 
      border-radius: var(--taas-radius); 
      font-size: 1rem;
    }
    .form-input:focus { outline: 2px solid var(--taas-accent); border-color: transparent; }

    /* UTILS */
    .text-accent { color: var(--taas-accent); }
    .grid-features {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      gap: 1.5rem;
    }
    .feature-card {
      background: var(--taas-bg);
      padding: 1.5rem;
      border: 1px solid var(--taas-border);
      border-radius: var(--taas-radius);
      display: flex;
      align-items: flex-start;
      gap: 1rem;
    }
  `;

    return (
        <div className="taas-wrapper">
            <style>{styles}</style>

            {/* HERO SECTION */}
            <section className="taas-hero">
                <div className="taas-container">
                    <div className="taas-badge">
                        <IconCheck /> Governance & Quality Included
                    </div>
                    <h1 className="taas-h1">Talent-as-a-Service<br />for Enterprises</h1>
                    <p className="taas-lead">
                        Accelerate your digital roadmap with manageable, high-performing development teams.
                        QA, Tech Lead supervision, and Weekly Governance included.
                    </p>
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                            className="taas-btn taas-btn-primary"
                            onClick={() => window.location.href = '/pricing'}
                        >
                            Calculate Pricing
                        </button>
                        <button
                            className="taas-btn taas-btn-outline"
                            onClick={() => scrollToSection('contact')}
                        >
                            Get Proposal
                        </button>
                    </div>
                    <div style={{ marginTop: '2rem', display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {['Enterprises', 'Non-tech companies', 'Fast Digitalisation', 'Project Backlogs'].map(tag => (
                            <span key={tag} style={{ fontSize: '0.8rem', background: '#f1f5f9', padding: '4px 12px', borderRadius: '4px', color: '#64748b' }}>
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* STICKY NAV */}
            <nav className="taas-nav">
                <div className="taas-container">
                    <ul className="taas-nav-list">
                        <li><a className="taas-nav-link" onClick={() => scrollToSection('problem')}>Why TaaS?</a></li>
                        <li><a className="taas-nav-link" onClick={() => scrollToSection('solution')}>Our Model</a></li>
                        <li><a className="taas-nav-link" onClick={() => scrollToSection('process')}>Process</a></li>
                        <li><a className="taas-nav-link" onClick={() => scrollToSection('comparison')}>Comparison</a></li>
                        <li><a className="taas-nav-link" onClick={() => scrollToSection('faq')}>FAQ</a></li>
                    </ul>
                </div>
            </nav>

            {/* PROBLEM SECTION */}
            <section id="problem" className="taas-section">
                <div className="taas-container">
                    <h2 className="taas-h2">The Hidden Pain of Internal Hiring</h2>
                    <p className="taas-lead" style={{ textAlign: 'center' }}>
                        Building an in-house team is slow, expensive, and risky.
                        Common challenges facing Malaysian enterprises:
                    </p>
                    <div className="problem-grid">
                        {[
                            { title: "Slow Hiring Cycle", text: "3-6 months to source, interview, and onboard quality talent." },
                            { title: "Talent Mismatch", text: "New hires may lack the specific skills needed for your stack." },
                            { title: "Resignation Risk", text: "Knowledge loss when key developers leave mid-project." },
                            { title: "Hidden Overheads", text: "Recruitment fees, equipment, insurance, and benefits add up." },
                            { title: "Management Burden", text: "Requires technical leadership to manage code quality daily." },
                            { title: "Budget Unpredictability", text: "Salaries fluctuate; replacement costs are unbudgeted." }
                        ].map((item, i) => (
                            <div key={i} className="problem-card">
                                <div className="problem-header"><IconX /> {item.title}</div>
                                <div style={{ fontSize: '0.95rem', color: '#64748b' }}>{item.text}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SOLUTION SECTION */}
            <section id="solution" className="taas-section taas-section-white">
                <div className="taas-container">
                    <h2 className="taas-h2">The CODEMAKER Solution</h2>
                    <p className="taas-lead" style={{ textAlign: 'center' }}>
                        A fully managed "Phase 1" delivery model designed for stability and quality.
                    </p>

                    <div className="grid-features">
                        {[
                            { title: "Managed Developers", text: "Junior & Senior talent vetted for your specific stack." },
                            { title: "Tech Lead Included", text: "Code review, architecture guidance, and mentorship built-in." },
                            { title: "QA Discipline", text: "Dedicated testing protocols to ensuring software reliability." },
                            { title: "Weekly Governance", text: "Transparent reporting on progress, velocity, and blockers." },
                            { title: "Replacement Policy", text: "Swift replacement guarantee if performance issues arise." },
                            { title: "Flexible Tenure", text: "Scale up or down; contracts from 3 to 24 months." }
                        ].map((feat, i) => (
                            <div key={i} className="feature-card">
                                <div style={{ color: 'var(--taas-success)' }}><IconCheck /></div>
                                <div>
                                    <h4 className="taas-h3" style={{ fontSize: '1.1rem', marginBottom: '0.25rem' }}>{feat.title}</h4>
                                    <p style={{ fontSize: '0.9rem', color: 'var(--taas-text-muted)', margin: 0 }}>{feat.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROCESS TIMELINE */}
            <section id="process" className="taas-section">
                <div className="taas-container">
                    <h2 className="taas-h2">How It Works</h2>
                    <div className="solution-timeline">
                        {[
                            { step: 1, title: "Discovery", text: "We analyze your requirements and tech stack." },
                            { step: 2, title: "Matching", text: "We select the right mix of Junior/Senior talent." },
                            { step: 3, title: "Onboarding", text: "Access setup, knowledge transfer, and kickoff." },
                            { step: 4, title: "Rhythm", text: "Weekly sprints, reports, and governance calls." },
                            { step: 5, title: "Scale/Renew", text: "Extend tenure or adjust headcount as needed." }
                        ].map((item) => (
                            <div key={item.step} className="timeline-step">
                                <div className="step-number">{item.step}</div>
                                <h4 style={{ fontWeight: 700, marginBottom: '0.5rem', color: 'var(--taas-primary)' }}>{item.title}</h4>
                                <p style={{ fontSize: '0.9rem', color: 'var(--taas-text-muted)', margin: 0 }}>{item.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* COMPARISON TABLE */}
            <section id="comparison" className="taas-section taas-section-white">
                <div className="taas-container">
                    <h2 className="taas-h2">Internal Hiring vs. CODEMAKER</h2>
                    <div className="comparison-table-wrapper">
                        <table className="taas-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '40%' }}>Feature</th>
                                    <th style={{ width: '30%' }}>Internal Hiring</th>
                                    <th style={{ width: '30%' }} className="col-codemaker">CODEMAKER TaaS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    { feat: "Time to Start", internal: "3 - 6 Months", taas: "2 - 4 Weeks" },
                                    { feat: "Recruitment Cost", internal: "15-20% of annual salary", taas: "Included (RM 0)" },
                                    { feat: "Technical Supervision", internal: "Requires hiring CTO/Lead", taas: "Tech Lead Supervision Included" },
                                    { feat: "Quality Assurance", internal: "Separate hire needed", taas: "QA Discipline Included" },
                                    { feat: "Replacement Risk", internal: "High (Restart hiring)", taas: "Replacement Policy Included" },
                                    { feat: "Governance", internal: "Self-managed", taas: "Weekly Reporting Provided" },
                                    { feat: "Cost Predictability", internal: "Variable (Bonuses, Benefits)", taas: "Fixed Monthly Fee" },
                                    { feat: "Scalability", internal: "Difficult (Retrenchment issues)", taas: "Flexible Contract Terms" },
                                ].map((row, i) => (
                                    <tr key={i}>
                                        <td style={{ fontWeight: 500 }}>{row.feat}</td>
                                        <td className="col-internal">{row.internal}</td>
                                        <td className="col-codemaker highlight-cell">{row.taas}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* PRICING PHILOSOPHY */}
            <section className="taas-section" style={{ textAlign: 'center' }}>
                <div className="taas-container">
                    <h2 className="taas-h2">Transparent Pricing</h2>
                    <p className="taas-lead">
                        We believe in complete transparency. Our pricing is based on headcount and tenure duration.
                        <br />Logic is simple: Longer tenure = Higher discount.
                    </p>
                    <div style={{ background: '#e0f2fe', display: 'inline-block', padding: '1rem 2rem', borderRadius: '8px', color: '#0369a1', fontWeight: 600 }}>
                        No hidden fees. No add-ons. Tech Lead & QA included.
                    </div>
                    <div style={{ marginTop: '2rem' }}>
                        <button className="taas-btn taas-btn-primary" onClick={() => window.location.href = '/pricing'}>
                            Try the Calculator
                        </button>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="taas-section taas-section-white">
                <div className="taas-container">
                    <h2 className="taas-h2">Frequently Asked Questions</h2>
                    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                        {[
                            { q: "What roles can I hire?", a: "We focus on Full Stack Developers (React, Node, Java, .NET) suitable for modern enterprise web applications." },
                            { q: "Can I hire only 1 developer?", a: "Yes, you can start with a single developer. However, we recommend a small team (e.g., 2 devs) for better continuity." },
                            { q: "How fast can we start?", a: "Once requirements are finalized, we typically onboard the team within 2 to 4 weeks." },
                            { q: "What does 'Tech Lead Supervision' mean?", a: "Our internal Tech Leads review code, set up architecture, and mentor the developers. You don't manage code quality alone." },
                            { q: "How does replacement work?", a: "If a developer is not performing or is a mismatch, we initiate a replacement process at no extra cost, subject to a handover period." },
                            { q: "Do you work with enterprise security requirements?", a: "Yes, we are experienced with banking and telco standards. We can work within your VPN/VDI environments." },
                            { q: "Can we change headcount during the contract?", a: "Yes, you can scale up anytime. Scaling down is subject to the notice period defined in the contract." },
                            { q: "Is this staff augmentation or outsourcing?", a: "It's a hybrid. You get the control of staff aug (direct access) with the support wrapper of outsourcing (management, QA, governance)." },
                        ].map((item, i) => (
                            <div key={i} className="faq-item">
                                <div className="faq-question" onClick={() => toggleFaq(i)}>
                                    {item.q}
                                    <IconChevronDown isOpen={openFaq === i} />
                                </div>
                                {openFaq === i && <div className="faq-answer">{item.a}</div>}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CONTACT FORM */}
            <section id="contact" className="taas-section" style={{ background: 'var(--taas-primary)', color: 'white' }}>
                <div className="taas-container">
                    <h2 className="taas-h2" style={{ color: 'white' }}>Start Your TaaS Journey</h2>
                    <p className="taas-lead" style={{ color: '#94a3b8', textAlign: 'center' }}>
                        Ready to stabilize your delivery? Request a proposal today.
                    </p>

                    <div className="form-card">
                        {formStatus === 'success' ? (
                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                                <div style={{ width: 60, height: 60, background: '#dcfce7', borderRadius: '50%', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                                    <IconCheck />
                                </div>
                                <h3 className="taas-h3">Request Received!</h3>
                                <p style={{ color: '#64748b' }}>We will be in touch shortly to discuss your requirements.</p>
                                <button
                                    className="taas-btn taas-btn-outline"
                                    onClick={() => setFormStatus('idle')}
                                >
                                    Send Another
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label className="form-label">Company Name</label>
                                    <input
                                        required
                                        name="company"
                                        value={formData.company}
                                        onChange={handleInputChange}
                                        className="form-input"
                                        placeholder="e.g. Acme Corp"
                                    />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div className="form-group">
                                        <label className="form-label">Name</label>
                                        <input
                                            required
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            className="form-input"
                                            placeholder="John Doe"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Phone</label>
                                        <input
                                            required
                                            name="phone"
                                            type="tel"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            className="form-input"
                                            placeholder="+60..."
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Email</label>
                                    <input
                                        required
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="form-input"
                                        placeholder="john@company.com"
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Brief Requirement</label>
                                    <textarea
                                        name="requirement"
                                        value={formData.requirement}
                                        onChange={handleInputChange}
                                        className="form-input"
                                        rows="3"
                                        placeholder="We are looking for..."
                                    ></textarea>
                                </div>
                                <button
                                    type="submit"
                                    className="taas-btn taas-btn-primary"
                                    style={{ width: '100%' }}
                                    disabled={formStatus === 'submitting'}
                                >
                                    {formStatus === 'submitting' ? 'Sending...' : 'Request Proposal'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>

        </div>
    );
};

export default TaasOverviewPage;
